import * as React from "react";

import { createContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Family,
  FetchAdvisorNotificationStatusesResponse_FamilyStatus,
  FetchAdvisorNotificationStatusesResponse_OnboardingModuleState,
} from "protogen/advisors_service_pb";
import { useFetchAdvisorNotificationStatuses } from "services/advisor";
import { useLocation } from "react-router-dom";
import { usePushContext } from "./PushContextProvider";
import useIsVisible from "../hooks/useIsVisible";
import { debounce } from "lodash";
import { useWebSocketContext } from "./SocketConsumerProvider";

type contentType = {
  families: Family[];
  familyStatus: Map<
    string,
    FetchAdvisorNotificationStatusesResponse_FamilyStatus
  >;
  badgeCount: number | null;
  emailUnreadCount: number;
  phoneUnreadCount: number;
  messageUnreadCount: number;
  taskUnreadCount: number;
  datebookUnreadCount: number;
  documentUnreadCount: number;
  familyHealthAlert: boolean;
  onboardingModuleState?: FetchAdvisorNotificationStatusesResponse_OnboardingModuleState;
  newClientOpportunitiesToggledSec: number;
  newClientOpportunitiesEnabled: boolean;
  // unreadForum: number;
  loaded: boolean;
  loading: boolean;
  lastLoadedTime: number;
  // Last updated
  // admin values
  adminLeadsActionNeededCount: number;
  adminApplicationActionNeededCount: number;
  adminRematchActiveCount: number;
  // delegation values
  delegationAvailableCount: number;
  delegationActiveCount: number;
  delegationUnreadCount: number;
};

const defaultContext = () => ({
  families: [],
  familyStatus: new Map(),
  badgeCount: null,
  emailUnreadCount: 0,
  phoneUnreadCount: 0,
  messageUnreadCount: 0,
  taskUnreadCount: 0,
  datebookUnreadCount: 0,
  documentUnreadCount: 0,
  onboardingModuleState: undefined,
  newClientOpportunitiesToggledSec: 0,
  newClientOpportunitiesEnabled: true,
  familyHealthAlert: false,
  // unreadForum: number;
  loaded: false,
  loading: false,
  lastLoadedTime: 0,
  // admin values
  adminLeadsActionNeededCount: 0,
  adminApplicationActionNeededCount: 0,
  adminRematchActiveCount: 0,
  // delegation values
  delegationAvailableCount: 0,
  delegationActiveCount: 0,
  delegationUnreadCount: 0,
});

interface FullContext extends contentType {
  refresh: () => void;
}
export const StatusContext = createContext<FullContext>({
  ...defaultContext(),
  refresh: () => {},
});

export default ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { registerHandler, unregisterHandler } = usePushContext();
  const lastLoadedTimeRef = useRef<number>(0);
  const [context, setContext] = useState<contentType>(defaultContext());
  const {
    registerHandler: registerWebSocketHandler,
    unregisterHandler: unregisterWebSocketHandler,
  } = useWebSocketContext();
  const { request, loading } = useFetchAdvisorNotificationStatuses((r) => {
    const familyStatus = new Map(
      (r.familyStatus || []).map((s) => [s.familyRef, s]),
    );
    // Let's not do this everytime, I think apple can get upset if you do this in iOS.
    if (r.badgeCount !== context.badgeCount && "setAppBadge" in navigator) {
      // @ts-ignore
      navigator.setAppBadge(r.badgeCount || 0);
    }
    lastLoadedTimeRef.current = Date.now();
    setContext({
      families: r.families,
      familyStatus: familyStatus,
      badgeCount: r.badgeCount,
      emailUnreadCount: r.emailUnreadCount,
      phoneUnreadCount: r.phoneUnreadCount,
      messageUnreadCount: r.messageUnreadCount,
      taskUnreadCount: r.taskUnreadCount,
      datebookUnreadCount: r.datebookUnreadCount,
      onboardingModuleState: r.onboardingModuleState,
      newClientOpportunitiesToggledSec: Number(
        r.newClientOpportunitiesToggledSec,
      ),
      newClientOpportunitiesEnabled: r.newClientOpportunitiesEnabled,
      documentUnreadCount: r.documentUnreadCount,
      // Check if any are true.
      familyHealthAlert: (r.familyStatus || []).some((s) => s.needsAttention),
      // It has been loaded at least once.
      loaded: true,
      loading: false,
      lastLoadedTime: lastLoadedTimeRef.current,
      // admin values
      adminLeadsActionNeededCount: r.adminLeadsActionNeededCount,
      adminApplicationActionNeededCount: r.adminApplicationActionNeededCount,
      adminRematchActiveCount: r.adminRematchActiveCount,
      // delegation values
      delegationAvailableCount: r.delegationAvailableCount,
      delegationActiveCount: r.delegationActiveCount,
      delegationUnreadCount: r.delegationUnreadCount,
    });
  });

  const refresh = (force: boolean = false) => {
    if (loading) {
      // Don't refresh if we are already loading.
      return;
    }
    const REFRESH_BUFFER_MS = 60000;
    // Check if we've refreshed within the last minute (unless forced)
    const now = Date.now();
    if (
      !force &&
      lastLoadedTimeRef.current &&
      now - lastLoadedTimeRef.current < REFRESH_BUFFER_MS
    ) {
      // Less than a minute has passed, skip refresh
      return;
    }

    request();
    setContext((prev) => ({
      ...prev,
      loading: true,
    }));
  };

  // Reload the state whenever the page is changed or we have re-opened the page after some period.
  // Add a debounce + delay. Delay is so that we can let users fire off quick events like markAsRead
  // and then we can consume the most up to date data (like the newest badge count).
  const debouncedRefresh = useMemo(
    () => debounce(refresh, 500, { trailing: true }), // Adjust the delay as needed (e.g., 1000ms = 1 second)
    [],
  );

  const debouncedRefreshForced = useMemo(
    () => debounce(() => refresh(true), 500, { trailing: true }),
    [],
  );

  useIsVisible({
    onRefocus: async (blurSecs) => {
      if (blurSecs > 10) {
        debouncedRefresh();
      }
    },
  });
  // This might need to get tweaked, but we don't want
  // to refetch on single location change since things like search params might change frequently.
  useEffect(debouncedRefresh, [location.pathname]);

  useEffect(() => {
    const channels = [
      "task/",
      "email/",
      "phonecall/",
      "textmessage/",
      "message/",
      "forum/",
    ];
    const pushHandler = () => {
      // On mobile we can have multiple tabs/layers open in the app. Upon a notification, all of these may be triggered.
      // Let's not refresh if the tab is hidden.
      if (!document.hidden) {
        debouncedRefreshForced();
      }
    };
    registerHandler(pushHandler);
    registerWebSocketHandler(channels, pushHandler);

    return () => {
      unregisterHandler(pushHandler);
      unregisterWebSocketHandler(channels, pushHandler);
    };
  }, [
    registerHandler,
    unregisterHandler,
    registerWebSocketHandler,
    unregisterWebSocketHandler,
    debouncedRefreshForced,
  ]);

  return (
    <StatusContext.Provider
      value={{
        ...context,
        refresh: () => debouncedRefresh(),
      }}
    >
      {children}
    </StatusContext.Provider>
  );
};
